/**
 * FORM GENERICO DI UN'AZIONE — i parametri che l'azione dichiara nel catalogo
 * (`AutomationActionType.params`, `getParams` lato Java), disegnati con lo stesso `ParamField` del
 * blocco QUANDO.
 *
 * Serve alle azioni che non hanno un form scritto a mano nel registry: tipicamente quelle dei
 * plugin, che il core non conosce. Chi un form proprio ce l'ha vince, e questo non si monta.
 *
 * **La forma è quella di `eventConfig`**: sotto il nome del parametro sta `{"value": …}` per un
 * valore singolo e `{"from": …, "to": …}` per un intervallo. Per questo la conversione la fanno gli
 * stessi mapper del QUANDO (`eventConfigToDto`/`eventConfigFromDto`), e il server la giudica con la
 * stessa regola.
 *
 * Tiene i valori GREZZI in uno stato locale, come il QUANDO, e passa alla modale quelli TIPIZZATI:
 * tipizzare il valore che l'input mostra renderebbe impossibile digitare un decimale ("1." diventa
 * 1 e il punto sparisce sotto le dita). Il rimontaggio al cambio azione lo garantisce la `key` della
 * modale.
 */

import { useState } from 'react';
import ParamField from '../ParamField';
import { eventConfigFromDto, eventConfigToDto } from '../automationMappers';
import type { AutomationParam } from '../automationTypes';
import type { ActionConfigProps } from './automationActionConfig';

interface DeclaredParamsActionConfigProps extends Omit<ActionConfigProps, 'className'> {
  /** Parametri dichiarati dall'azione, dal catalogo ALLORA. */
  params: AutomationParam[];
}

/**
 * Valori dell'editor -> config. Le chiavi che nessun parametro dichiara si conservano; un parametro
 * vuoto si omette (lo fa `eventConfigToDto`), così il server lo vede "non configurato" —
 * violazione solo se obbligatorio — invece di ricevere un valore sbagliato.
 */
function configFromRaw(
  params: AutomationParam[],
  raw: Record<string, unknown>,
  config: Record<string, unknown> | null,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(config ?? {}) };

  for (const param of params) delete next[param.name];

  return { ...next, ...(JSON.parse(eventConfigToDto(params, raw)) as Record<string, unknown>) };
}

export default function DeclaredParamsActionConfig({
  params,
  config,
  onChange,
}: DeclaredParamsActionConfigProps) {
  const [raw, setRaw] = useState<Record<string, unknown>>(() =>
    eventConfigFromDto(config ? JSON.stringify(config) : null),
  );

  const change = (param: AutomationParam, value: unknown) => {
    const nextRaw = { ...raw, [param.name]: value };

    setRaw(nextRaw);
    onChange(configFromRaw(params, nextRaw, config));
  };

  return (
    <div className="builder-crow">
      {params.map((param) => (
        <ParamField
          key={param.name}
          param={param}
          value={raw[param.name]}
          onChange={(value) => change(param, value)}
        />
      ))}
    </div>
  );
}
